/**
 * Does Nexsoss know? (twin slice 1, plan W5 step 9; ideation §7).
 *
 * Every item in the attention list carries exactly one evidence line, and
 * it must only claim what the alert data backs:
 *
 *   cant_check      the alerts list could not be read, or it was a capped
 *                   page and the matching alert may be past its end
 *   muted           alerts for this device are switched off, so the
 *                   server will not raise one
 *   pending_server  the server raises an alert for this, and has not had
 *                   its own dwell (plus two alert polls) yet
 *   unknown         no open alert, and none is coming: "Nexsoss may not
 *                   know. Please call."
 *   raised          an open alert nobody has acknowledged; after 5 min it
 *                   asks to call (Decision 8), unless the reader's steps
 *                   say when to (`asksNoCall`)
 *   picked_up       acknowledged by someone counted as Nexsoss staff
 *   seen            acknowledged by anyone else, which stays visible
 *
 * WHO COUNTS AS NEXSOSS (Q3). `ackedByRoles` intersected with
 * `NEXSOSS_STAFF_ROLES` (platform_admin, support_l2, support_l3), falling
 * back to the single `ackedByRole`. `service_operator` is NOT staff: some
 * hotel staff hold it. An ack with no role at all (one made before
 * migration 0035, or by a server without it) reads "Seen by" -- never
 * "Picked up", because nothing says who it was.
 *
 * WHO ACKED, in words. The acker's email when the row carries one; the
 * subject id never (a ZITADEL id reads "Seen by 312845901723460123"):
 * without an email a staff ack reads "Picked up by Nexsoss" -- its role
 * says so -- and any other "Seen by a team member".
 *
 * Times are on the site's clock, labelled "(device time)" when the site
 * has no declared zone, and name their day when it is not today
 * (`dayClockText`): an alert raised last night never reads as this
 * morning's.
 */
import { isNexsossStaff, type Audience } from '../../auth/roles';
import { alertFingerprint, alertRule, type AttentionAlert } from './alert-subject';
import { dayClockText, type SiteTimeZone } from './words';

export type EvidenceTier =
  | 'cant_check'
  | 'muted'
  | 'pending_server'
  | 'unknown'
  | 'raised'
  | 'picked_up'
  | 'seen';

/** One alert that would mean "the server has raised this". */
export interface AlertKey {
  rule: string;
  sourceKind: string;
  sourceId: string;
}

export interface AlertsReading {
  /** False when the list could not be read at all. */
  ok: boolean;
  /** The open rows this page holds. */
  rows: readonly AttentionAlert[];
  /** The list is a capped page and the server holds more open rows than
   *  it returned (`summary.open > rows.length`, or the page is full). */
  truncated: boolean;
}

/** A per-device mute, as `GET .../alerts/mutes` serves it. */
export interface AlertMuteLike {
  source_kind: string;
  source_id: string;
  /** Null = until unmuted. */
  expires_at_ms: number | null;
}

/**
 * How long after a condition starts the server's rule opens its alert.
 *
 * `defaultOn: false` marks the rules that ship OFF (Q8), each behind its
 * own flag: W7's two, `NEXSOSS_LIFT_LANES_CLOSED_ENABLED` /
 * `NEXSOSS_LIFT_NO_READING_ENABLED`, and A14's lift-control rule,
 * `NEXSOSS_LIFT_GATE_DISARMED_ENABLED` (`tests/fixtures/contracts/alert-
 * rules.json` holds the list equal to the engine's flags). Until the
 * caller says they are on, no item waits on them: promising "Nexsoss is
 * told automatically" for a rule that is off would be the exact lie this
 * tier exists to avoid. The values are W7's (plan X26): 120 s grace for
 * lanes closed; no-reading opens 30 s after the reading goes stale.
 * `lift_gate_disarmed` opens after the gate has read shut for 120 s
 * (`_LIFT_GATE_DISARMED_GRACE_S`, held equal by the engine's tests), and
 * never while a planned maintenance window is open, so the selector
 * promises nothing for it then. `task_attention`'s 120 s is
 * `NEXSOSS_TASK_ATTENTION_ALERT_AFTER_S`'s default in `alerts_engine.py`.
 * `lift_fault` opens at once for an emergency mode and after
 * `_LIFT_OFF_GRACE_S` (15 s) for a car reading offline; the longer is the
 * one an item may wait on. `edge_box_offline` opens after the box has been
 * silent for `edge_box.OFFLINE_AFTER_S_DEFAULT` (held equal by the engine's
 * tests).
 */
export const SERVER_RULE_DWELLS: Readonly<Record<string, { dwellMs: number; defaultOn: boolean }>> =
  {
    robot_issue: { dwellMs: 0, defaultOn: true },
    robot_error: { dwellMs: 0, defaultOn: true },
    robot_unreachable: { dwellMs: 0, defaultOn: true },
    lift_refusal: { dwellMs: 0, defaultOn: true },
    lift_fault: { dwellMs: 15_000, defaultOn: true },
    task_attention: { dwellMs: 120_000, defaultOn: true },
    lift_lanes_closed: { dwellMs: 120_000, defaultOn: false },
    lift_no_reading: { dwellMs: 30_000, defaultOn: false },
    lift_gate_disarmed: { dwellMs: 120_000, defaultOn: false },
    edge_box_offline: { dwellMs: 180_000, defaultOn: true },
  };

/** Allowance for a freshly opened alert to reach this page: two 15 s
 *  alert polls. */
export const ALERT_REACH_MS = 30_000;

/** An unacknowledged alert asks to be called about after this long
 *  (Decision 8). */
export const PLEASE_CALL_AFTER_MS = 5 * 60_000;

export interface EvidenceInput {
  /** The alerts that would mean the server has raised this item. */
  keys: readonly AlertKey[];
  /** Fingerprints the item matches exactly (a robot issue's category, a
   *  lift refusal's code). When given, a key's rule/source match is not
   *  enough: the fingerprint must match too. */
  fingerprints?: readonly string[];
  alerts: AlertsReading;
  /** Null when the mutes list could not be read. */
  mutes: readonly AlertMuteLike[] | null;
  /** When this page first saw the condition (device clock). */
  seenSinceMs: number | null;
  nowMs: number;
  audience: Audience;
  clock: SiteTimeZone;
  /** Rules switched on that are off by default (W7's). */
  enabledRules?: ReadonlySet<string>;
  /** The reader's own steps say when to call (`siteFixesFirst`): an
   *  overdue alert still says nobody has picked it up, but does not ask
   *  for the call its steps put last. */
  asksNoCall?: boolean;
}

export interface Evidence {
  tier: EvidenceTier;
  /** The one line the card shows, in the audience's words. */
  text: string;
  /** The card asks the reader to call Nexsoss. */
  pleaseCall: boolean;
  /** Only `picked_up` collapses into "Picked up (n)". */
  collapses: boolean;
  /** The alert this rests on, when there is one. */
  alertId: string | null;
}

const PLEASE_CALL_NEXSOSS = 'Please call Nexsoss.';
/** After a sentence that already names Nexsoss. */
const PLEASE_CALL = 'Please call.';

function matches(alert: AttentionAlert, input: EvidenceInput): boolean {
  const rule = alertRule(alert);
  const source = alert.sourceId || alert.source;
  const keyed = input.keys.some(
    (k) =>
      k.rule === rule &&
      k.sourceId === source &&
      (alert.sourceKind === undefined || alert.sourceKind === k.sourceKind),
  );
  if (!keyed) return false;
  if (!input.fingerprints?.length) return true;
  const fp = alertFingerprint(alert);
  // A row whose fingerprint cannot be read for a rule that needs one is
  // not evidence about THIS category or code.
  return fp !== null && input.fingerprints.includes(fp);
}

function isAcked(a: AttentionAlert): boolean {
  return typeof a.ackedAtMs === 'number' || Boolean(a.ackedBySub);
}

/** "Picked up" needs a staff role on the ack; no role is never staff. */
export function ackedByNexsoss(a: AttentionAlert): boolean {
  if (a.ackedByRoles && a.ackedByRoles.length > 0) return isNexsossStaff(a.ackedByRoles);
  if (a.ackedByRole) return isNexsossStaff([a.ackedByRole]);
  return false;
}

/** Who acked, for "Picked up by" / "Seen by": never a raw user id. */
export const ACKER_STAFF = 'Nexsoss';
export const ACKER_ANYONE = 'a team member';

function ackerName(a: AttentionAlert, staff: boolean): string {
  return a.ackedByEmail || (staff ? ACKER_STAFF : ACKER_ANYONE);
}

function at(ms: number | undefined, input: EvidenceInput): string {
  return typeof ms === 'number' && Number.isFinite(ms)
    ? ` ${dayClockText(ms, input.clock, input.nowMs)}`
    : '';
}

function ruleOn(rule: string, enabled: ReadonlySet<string> | undefined): boolean {
  const known = SERVER_RULE_DWELLS[rule];
  if (!known) return false;
  return known.defaultOn || Boolean(enabled?.has(rule));
}

function muted(input: EvidenceInput): boolean {
  if (!input.mutes) return false;
  return input.mutes.some(
    (m) =>
      (m.expires_at_ms === null || m.expires_at_ms > input.nowMs) &&
      input.keys.some((k) => k.sourceKind === m.source_kind && k.sourceId === m.source_id),
  );
}

function fromAlert(a: AttentionAlert, input: EvidenceInput): Evidence {
  const ops = input.audience === 'ops';
  if (isAcked(a)) {
    const nexsoss = ackedByNexsoss(a);
    const verb = nexsoss ? 'Picked up' : 'Seen';
    return {
      tier: nexsoss ? 'picked_up' : 'seen',
      text: `${verb} by ${ackerName(a, nexsoss)}${at(a.ackedAtMs, input)}`,
      pleaseCall: false,
      collapses: nexsoss,
      alertId: a.id,
    };
  }
  const opened = typeof a.openedAtMs === 'number' ? a.openedAtMs : null;
  // The server stamped `openedAtMs`; a device clock running behind it reads
  // a negative age, which is "just raised", never "overdue".
  const overdue = opened !== null && input.nowMs - opened >= PLEASE_CALL_AFTER_MS;
  // Overdue, a reader whose steps end with the call reads the same line as
  // before it: the line still says nobody has it, the steps say when.
  const asks = overdue && input.asksNoCall !== true;
  const head = `Alert raised${at(opened ?? undefined, input)}, ${
    ops ? 'not acknowledged' : 'not yet picked up'
  }.`;
  return {
    tier: 'raised',
    text: asks ? `${head} ${PLEASE_CALL_NEXSOSS}` : head,
    pleaseCall: asks,
    collapses: false,
    alertId: a.id,
  };
}

const TIER_ORDER: Readonly<Record<string, number>> = { picked_up: 0, seen: 1, raised: 2 };

export function evidenceFor(input: EvidenceInput): Evidence {
  const ops = input.audience === 'ops';
  const none = { collapses: false, alertId: null } as const;

  if (!input.alerts.ok) {
    return {
      ...none,
      tier: 'cant_check',
      text: ops
        ? "Can't check: the alerts list could not be read."
        : `Can't check whether Nexsoss has been told. ${PLEASE_CALL}`,
      pleaseCall: true,
    };
  }

  const found = input.alerts.rows
    .filter((a) => matches(a, input))
    .map((a) => fromAlert(a, input))
    .sort((x, y) => (TIER_ORDER[x.tier] ?? 9) - (TIER_ORDER[y.tier] ?? 9));
  if (found.length > 0) return found[0]!;

  if (muted(input)) {
    return {
      ...none,
      tier: 'muted',
      text: ops
        ? 'Muted: the server raises no alert for this device.'
        : `Alerts for this are switched off, so Nexsoss may not know. ${PLEASE_CALL}`,
      pleaseCall: true,
    };
  }

  const live = input.keys.filter((k) => ruleOn(k.rule, input.enabledRules));
  if (live.length > 0 && input.seenSinceMs !== null) {
    const waitMs = Math.min(...live.map((k) => SERVER_RULE_DWELLS[k.rule]!.dwellMs)) + ALERT_REACH_MS;
    const age = input.nowMs - input.seenSinceMs;
    if (age >= 0 && age < waitMs) {
      return {
        ...none,
        tier: 'pending_server',
        text: ops
          ? `The server raises ${live.map((k) => k.rule).join(' or ')} for this; not seen yet.`
          : 'Nexsoss is told automatically if this carries on.',
        pleaseCall: false,
      };
    }
  }

  if (live.length > 0 && input.alerts.truncated) {
    // The page is capped and the server holds more open rows than it sent:
    // the alert for this may simply be past the end of it.
    return {
      ...none,
      tier: 'cant_check',
      text: ops
        ? "Can't check: the alerts list is longer than one page."
        : `Can't check whether Nexsoss has been told. ${PLEASE_CALL}`,
      pleaseCall: true,
    };
  }

  return {
    ...none,
    tier: 'unknown',
    text: ops ? 'No open alert for this.' : `Nexsoss may not know. ${PLEASE_CALL}`,
    pleaseCall: true,
  };
}
